/**
 * <CredentialingPipelineWidget> — clinician-dashboard tile (Feb 2026).
 *
 * Surfaces the credentialing engine on the clinician dashboard so the
 * "we manage your credentialing end-to-end" pitch claim is visible to
 * the clinician who's paying for it. Renders a 3-column summary
 * (approved / in-progress / expiring soon), then the first in-flight
 * case with its checklist + a one-tap "Open credentialing" CTA.
 *
 * Reads:
 *   GET /api/clinician/credentialing  → {summary, cases, expirables}
 *
 * Honest framing: when nothing is approved yet, we render an
 * "open your first credentialing case" empty state (not a fake "ready"
 * pill) — `credentialing_ready` strictly reflects payer reality.
 */
import React, { useEffect, useState } from 'react';
import axios from '../../lib/api';
import { ShieldCheck, FileText, AlertTriangle, ArrowRight, CheckCircle2, Circle } from 'lucide-react';
import { TileHeader } from './BentoWidgets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_LABEL = {
  intake: 'Intake',
  data_collection: 'Gathering docs',
  caqh_attested: 'CAQH attested',
  submitted_to_payer: 'Submitted',
  payer_review: 'Payer review',
  approved: 'Approved',
  denied: 'Denied',
};

const STAGE_PCT = {
  intake: 10,
  data_collection: 30,
  caqh_attested: 55,
  submitted_to_payer: 75,
  payer_review: 90,
  approved: 100,
  denied: 100,
};

export const CredentialingPipelineWidget = ({ getAuthHeader }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/clinician/credentialing`, getAuthHeader?.());
        if (active) setData(r.data);
      } catch (e) {
        if (active) setErr(e?.response?.data?.detail || 'Unable to load credentialing.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [getAuthHeader]);

  const summary = data?.summary || { payers_approved: 0, in_progress: 0, expiring_soon: 0 };
  const firstActive = (data?.cases || []).find((c) => c.status === 'active');

  return (
    <div data-testid="credentialing-widget">
      <TileHeader
        eyebrow="Credentialing"
        title="Payer enrollment pipeline"
        action={
          <a
            href="/clinician/credentialing"
            className="text-sm font-semibold text-cn-primary inline-flex items-center gap-1.5 hover:underline"
            data-testid="credentialing-widget-open"
          >
            Open <ArrowRight className="w-4 h-4" />
          </a>
        }
      />

      {/* Three KPIs */}
      <div className="grid grid-cols-3 gap-3 mt-5">
        <div className="px-3 py-3 rounded-xl border border-cn-success/30 bg-cn-success/8 text-center">
          <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">Approved</p>
          <p className="text-2xl font-display font-extrabold text-cn-success mt-1"
             data-testid="credentialing-widget-approved">{summary.payers_approved}</p>
        </div>
        <div className="px-3 py-3 rounded-xl border border-cn-primary/30 bg-cn-primary/8 text-center">
          <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">In progress</p>
          <p className="text-2xl font-display font-extrabold text-cn-primary mt-1"
             data-testid="credentialing-widget-in-progress">{summary.in_progress}</p>
        </div>
        <div className={`px-3 py-3 rounded-xl text-center ${summary.expiring_soon > 0
          ? 'border border-cn-accent/40 bg-cn-accent/10'
          : 'border border-cn-border/60 bg-cn-bg/60'}`}>
          <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">Expiring &lt;90d</p>
          <p className={`text-2xl font-display font-extrabold mt-1 ${summary.expiring_soon > 0 ? 'text-cn-accent' : 'text-cn-text-muted'}`}
             data-testid="credentialing-widget-expiring">{summary.expiring_soon}</p>
        </div>
      </div>

      {/* Body */}
      <div className="mt-5">
        {loading && (
          <div className="h-24 rounded-xl bg-cn-bg/60 border border-cn-border/60 animate-pulse" />
        )}
        {!loading && err && (
          <p className="text-xs text-cn-text-muted">{err}</p>
        )}
        {!loading && !err && !firstActive && (
          <div className="p-4 rounded-xl border border-dashed border-cn-border/70 bg-cn-bg/60">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-cn-primary mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-cn-text dark:text-white">
                  Open your first credentialing case
                </p>
                <p className="text-xs text-cn-text-muted mt-1">
                  CaraLoom tracks your CAQH attestation, payer submission, and re-credentialing alerts
                  — so you can stay on top of every payer relationship.
                </p>
                <a href="/clinician/credentialing"
                   className="cn-btn cn-btn-primary !py-2 !px-3 !text-xs inline-flex items-center gap-1.5 mt-3"
                   data-testid="credentialing-widget-start">
                  <FileText className="w-3.5 h-3.5" /> Get started
                </a>
              </div>
            </div>
          </div>
        )}
        {!loading && !err && firstActive && (
          <div className="p-4 rounded-xl border border-cn-border/70 bg-white dark:bg-cn-ink-800" data-testid="credentialing-widget-active-case">
            <div className="flex items-center justify-between gap-2 mb-2">
              <p className="text-sm font-semibold text-cn-text dark:text-white truncate">{firstActive.payer_name}</p>
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-cn-primary/10 text-cn-primary shrink-0">
                {STAGE_LABEL[firstActive.stage] || firstActive.stage}
              </span>
            </div>
            <div className="w-full h-2 rounded-full bg-cn-bg/80 overflow-hidden">
              <div className="h-full bg-cn-primary transition-all"
                   style={{ width: `${STAGE_PCT[firstActive.stage] || 0}%` }} />
            </div>
            <p className="text-[11px] text-cn-text-muted mt-2">
              {(firstActive.checklist || []).filter((i) => i.done).length}
              {' / '}
              {(firstActive.checklist || []).filter((i) => i.required).length} required docs complete
            </p>
            <ul className="mt-3 space-y-1.5">
              {(firstActive.checklist || []).filter((i) => i.required).slice(0, 4).map((i) => (
                <li key={i.key} className="flex items-center gap-2 text-xs">
                  {i.done
                    ? <CheckCircle2 className="w-3.5 h-3.5 text-cn-success shrink-0" />
                    : <Circle className="w-3.5 h-3.5 text-cn-text-muted shrink-0" />}
                  <span className={i.done ? 'text-cn-text-muted line-through' : 'text-cn-text dark:text-white'}>
                    {i.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Expiring credentials nudge */}
        {!loading && (data?.expirables || []).some((e) => e.alert) && (
          <div className="mt-3 p-3 rounded-xl border border-cn-accent/30 bg-cn-accent/8 flex items-start gap-2"
               data-testid="credentialing-widget-expiring-banner">
            <AlertTriangle className="w-4 h-4 text-cn-accent shrink-0 mt-0.5" />
            <div className="text-xs">
              <p className="font-semibold text-cn-accent">
                {(data.expirables.filter((e) => e.alert)).length} credential(s) expire within 90 days
              </p>
              <p className="text-cn-text-muted mt-0.5">
                Refresh before they expire to keep your payer enrollments active.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CredentialingPipelineWidget;
